import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";

// Get AI Workflow Plans
export const getActiveAiWorkflowPlan = createAsyncThunk(
  "aiWorkflow/getActiveAiWorkflowPlan",
  async (_, { getState }) => {
    const { auth } = getState();
    const userId = auth.user?._id || auth.user?.id;

    if (!userId) {
      throw new Error("User ID not found");
    }

    const response = await api.get(`ai-workflow/subscriptions/user/${userId}`);
    return response.data;
  },
);

// Get AI Workflow Plan by Id
export const getAiWorkflowPlanById = createAsyncThunk(
  "aiWorkflow/getAiWorkflowPlanById",
  async (planId) => {
    const response = await api.get(`ai-workflow/subscriptions/${planId}`);
    return response.data;
  },
);

// =========== AI Workflow Comments ===========

// Get AI Workflow Comments
export const getAiWorkflowComments = createAsyncThunk(
  "aiWorkflow/getAiWorkflowComments",
  async (planId) => {
    const response = await api.get(
      `ai-workflow/subscriptions/${planId}/comments`,
    );
    return response.data;
  },
);

// Add AI Workflow Comment
export const addAiWorkflowComment = createAsyncThunk(
  "aiWorkflow/addAiWorkflowComment",
  async (commentData) => {
    const response = await api.post(
      "ai-workflow/subscriptions/comment",
      commentData,
    );
    return response.data;
  },
);

// Add AI Workflow Reply
export const addAiWorkflowReply = createAsyncThunk(
  "aiWorkflow/addAiWorkflowReply",
  async ({ commentId, replyData }) => {
    const response = await api.post(
      `ai-workflow/subscriptions/comment/${commentId}/reply`,
      replyData,
    );
    return response.data;
  },
);

// ======= AI Workflow Team =======

// Get Available AI Workflow Team
export const getAvailableAiWorkflowTeam = createAsyncThunk(
  "aiWorkflow/getAvailableAiWorkflowTeam",
  async (planId) => {
    const response = await api.get(
      `ai-workflow/subscriptions/${planId}/available-team-members`,
    );
    return response.data;
  },
);

// Add AI Workflow Team
export const addAiWorkflowTeam = createAsyncThunk(
  "aiWorkflow/addAiWorkflowTeam",
  async ({ projectId, teamMembers }) => {
    const payload = { teamMembers };
    const response = await api.post(
      `ai-workflow/subscriptions/${projectId}/team-members`,
      payload,
    );
    return response.data;
  },
);

// Remove AI Workflow Team
export const removeAiWorkflowTeam = createAsyncThunk(
  "aiWorkflow/removeAiWorkflowTeam",
  async (teamId) => {
    const response = await api.delete(
      `ai-workflow/subscriptions/team-members/${teamId}`,
    );
    return response.data;
  },
);

// ======= AI Workflow Tickets =======

// Raise AI Workflow Ticket
export const raiseAiWorkflowTicket = createAsyncThunk(
  "aiWorkflow/raiseAiWorkflowTicket",
  async ({ projectId, category, title, description, attachments = [] }) => {
    const response = await api.post(
      `ai-workflow/subscriptions/${projectId}/raise-ticket`,
      { category, title, description, attachments },
    );
    return response.data;
  },
);

// Add Comment to AI Workflow Ticket
export const addAiWorkflowTicketComment = createAsyncThunk(
  "aiWorkflow/addAiWorkflowTicketComment",
  async ({
    ticketId,
    content,
    attachments = [],
    isNestedReply = false,
    parentCommentId = null,
  }) => {
    const response = await api.post(
      `ai-workflow/subscriptions/tickets/${ticketId}/comments`,
      { content, attachments, isNestedReply, parentCommentId },
    );
    return response.data;
  },
);

// ====== AI Workflow File Attachments ======

// Add File Attachment to AI Workflow Ticket
export const addAiWorkflowFileAttachment = createAsyncThunk(
  "aiWorkflow/addAiWorkflowFileAttachment",
  async ({ projectId, files, note, category }) => {
    const response = await api.post(
      `ai-workflow/subscriptions/${projectId}/upload-files`,
      { files, note, category },
    );
    return response.data;
  },
);

// Cancel AI Workflow Subscription
export const cancelAiWorkflowSubscription = createAsyncThunk(
  "aiWorkflow/cancelAiWorkflowSubscription",
  async ({ subscriptionId, cancelReason, cancelReasonDetails }) => {
    const response = await api.post("ai-workflow/subscriptions/cancel", {
      subscriptionId,
      cancelReason,
      cancelReasonDetails,
    });
    return response.data;
  },
);
